/**
 * Transport-agnostic OpenAI Realtime function-call loop.
 *
 * Everything OpenAI-specific about *events* lives here, behind a tiny
 * `RealtimeTransport` interface, so the same loop is driven by a real WebRTC
 * data channel in the browser and by a fake transport in integration tests
 * (and so a different realtime provider could be swapped in later).
 */
import {
  DELEGATE_TOOL_NAME,
  DelegateResultSchema,
  type DebugEvent,
  type DebugEventType,
  type DelegateResult,
} from "./index.ts";
import { validatorsFor } from "./tools.ts";

/**
 * Argument schema per tool name, from the registry so a new tool needs no
 * change here. Unlisted tools pass their parsed args through.
 */
export const DEFAULT_TOOL_VALIDATORS: Record<string, { safeParse: (v: unknown) => any }> =
  validatorsFor();

export interface RealtimeTransport {
  /** Send a client event (already a plain object) to the Realtime session. */
  send(event: Record<string, unknown>): void;
}

export type ToolCall = { callId: string; name: string; rawArguments: string };

export type ToolExecutor = (call: {
  callId: string;
  name: string;
  args: unknown;
}) => Promise<DelegateResult>;

export type LoopOptions = {
  transport: RealtimeTransport;
  execute: ToolExecutor;
  /** A number, or a function of the call - web lookups need far longer than a local read. */
  timeoutMs?: number | ((ctx: { name: string; args: unknown }) => number);
  validators?: Record<string, { safeParse: (v: unknown) => any }>;
  onDebug?: (e: Omit<DebugEvent, "timestamp" | "localSessionId">) => void;
};

const safeResult = (instruction: string): DelegateResult => ({
  success: false,
  answerMode: "cannot_complete",
  facts: [],
  instructionToRealtime: instruction,
});

/**
 * Pull any function calls out of a Realtime server event.
 *
 * FUNCTION-CALL RECEIVER — event schema per
 * https://platform.openai.com/docs/api-reference/realtime
 *   - `response.function_call_arguments.done` -> { call_id, name, arguments }
 *   - `response.done` -> { response: { output: [{ type:"function_call", call_id, name, arguments }] } }
 * Both are handled; `handledCallIds` makes the duplicate a no-op.
 */
export function extractToolCalls(event: any): ToolCall[] {
  if (!event || typeof event !== "object") return [];

  if (event.type === "response.function_call_arguments.done" && event.call_id) {
    return [
      {
        callId: String(event.call_id),
        name: String(event.name ?? DELEGATE_TOOL_NAME),
        rawArguments: String(event.arguments ?? "{}"),
      },
    ];
  }

  if (event.type === "response.done" && Array.isArray(event.response?.output)) {
    // A response the user interrupted still arrives here, carrying whatever was
    // generated so far - including function-call arguments truncated mid-string.
    // Executing those produced malformed-arg errors, and because the call id was
    // already marked handled, the *complete* call that followed was discarded as
    // a duplicate. Only completed responses carry real calls.
    if (event.response?.status && event.response.status !== "completed") return [];

    return event.response.output
      .filter((item: any) => item?.type === "function_call" && item?.call_id)
      .map((item: any) => ({
        callId: String(item.call_id),
        name: String(item.name ?? DELEGATE_TOOL_NAME),
        rawArguments: String(item.arguments ?? "{}"),
      }));
  }

  return [];
}

export class RealtimeFunctionCallLoop {
  private handledCallIds = new Set<string>();
  private inFlight = 0;
  private responseActive = false;
  private pendingResponseRequest = false;

  constructor(private readonly opts: LoopOptions) {}

  get pendingCount() {
    return this.inFlight;
  }

  hasHandled(callId: string) {
    return this.handledCallIds.has(callId);
  }

  private debug(
    eventType: DebugEventType,
    extra: Partial<Omit<DebugEvent, "timestamp" | "localSessionId" | "eventType">> = {},
  ) {
    this.opts.onDebug?.({ eventType, ...extra });
  }

  /** Feed every server event here. Returns the calls it decided to execute. */
  handleEvent(event: any): Promise<void>[] {
    // Tracked so we never ask for a response while one is already running -
    // that returns "Conversation already has an active response in progress".
    if (event?.type === "response.created") this.responseActive = true;
    if (event?.type === "response.done") {
      this.responseActive = false;
      // A request deferred while that response was in flight fires now. It must
      // be DEFERRED, never dropped: a fast tool finishes before response.done
      // arrives, and dropping the request leaves the model silent forever.
      if (this.pendingResponseRequest) {
        this.pendingResponseRequest = false;
        this.opts.transport.send({ type: "response.create" });
      }
    }

    return extractToolCalls(event)
      .filter((call) => {
        if (this.handledCallIds.has(call.callId)) {
          this.debug("RAW", {
            toolCallId: call.callId,
            payloadSummary: "duplicate call id ignored",
          });
          return false;
        }
        this.handledCallIds.add(call.callId);
        return true;
      })
      .map((call) => this.runCall(call));
  }

  private async runCall(call: ToolCall): Promise<void> {
    const started = Date.now();
    this.inFlight += 1;
    this.debug("TOOL_CALL", { toolCallId: call.callId, toolName: call.name });

    let result: DelegateResult;
    try {
      let parsedArgs: unknown;
      let truncated = false;
      try {
        parsedArgs = JSON.parse(call.rawArguments || "{}");
      } catch {
        parsedArgs = undefined;
        // Unterminated JSON means the stream was cut off, not that the model
        // produced nonsense. Let the complete version of this call through.
        truncated = /[{[,:]\s*$|"[^"]*$/.test(call.rawArguments.trim());
        if (truncated) this.handledCallIds.delete(call.callId);
      }
      if (truncated) {
        this.debug("TOOL_ERROR", {
          toolCallId: call.callId,
          payloadSummary: "arguments truncated (response interrupted); ignoring this copy",
        });
        this.inFlight -= 1;
        return;
      }
      this.debug("TOOL_ARGS", { toolCallId: call.callId, payloadSummary: parsedArgs ?? call.rawArguments });

      const validator =
        (this.opts.validators ?? DEFAULT_TOOL_VALIDATORS)[call.name] ??
        ({ safeParse: (v: unknown) => ({ success: true, data: v }) } as any);
      const validated = validator.safeParse(parsedArgs);
      if (!validated.success) {
        this.debug("TOOL_ERROR", {
          toolCallId: call.callId,
          payloadSummary: { invalidArgs: validated.error?.issues?.map((i: any) => i.message) ?? "invalid" },
        });
        result = safeResult(
          "The delegate request was malformed, so nothing was looked up. Tell the user you could not complete that and ask them to rephrase.",
        );
      } else {
        const budget =
          typeof this.opts.timeoutMs === "function"
            ? this.opts.timeoutMs({ name: call.name, args: validated.data })
            : (this.opts.timeoutMs ?? 10_000);
        result = await withTimeout(
          this.opts.execute({ callId: call.callId, name: call.name, args: validated.data }),
          budget,
        );
        const checked = DelegateResultSchema.safeParse(result);
        if (!checked.success) {
          throw new Error("Delegate returned a result that failed schema validation");
        }
        result = checked.data;
        this.debug(result.facts.length > 0 ? "MEMORY_HIT" : "MEMORY_MISS", {
          toolCallId: call.callId,
          payloadSummary: result.facts.map((f) => f.key),
        });
      }
    } catch (err) {
      this.debug("TOOL_ERROR", {
        toolCallId: call.callId,
        payloadSummary: err instanceof Error ? err.message : String(err),
      });
      result = safeResult(
        "The Delegate brain could not be reached, so nothing was confirmed. Tell the user briefly and truthfully that you could not check right now.",
      );
    } finally {
      this.inFlight -= 1;
    }

    const durationMs = Date.now() - started;
    this.debug("TOOL_RESULT", {
      toolCallId: call.callId,
      toolName: call.name,
      durationMs,
      payloadSummary: result,
    });
    this.submitToolOutput(call.callId, result);
  }

  /**
   * FUNCTION-OUTPUT SENDER — client event schema per
   * https://platform.openai.com/docs/api-reference/realtime
   *   conversation.item.create { item: { type:"function_call_output", call_id, output } }
   * followed by `response.create` to let the model speak using the result.
   */
  submitToolOutput(callId: string, result: DelegateResult) {
    this.opts.transport.send({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: callId,
        output: JSON.stringify(result),
      },
    });
    if (this.responseActive) {
      this.pendingResponseRequest = true;
    } else {
      this.opts.transport.send({ type: "response.create" });
    }
  }
}

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Delegate timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
